/**
 * useDocumentDownload — F-006 Phase 5 (IMPL-P5-004).
 *
 * State machine for a single download attempt:
 *   idle → requesting → idle (on success, then `window.location.assign(url)`)
 *                    └→ error (UI shows retry button)
 *
 * CRITICAL: we use `window.location.assign(url)` (NOT `window.open(url)`):
 *  - `window.open` is usually popup-blocked when not triggered directly by a
 *    user gesture (which we lose after the async mutation completes).
 *  - `assign()` navigates the current tab; the server returns the file with
 *    a `Content-Disposition: attachment` header so the browser triggers the
 *    download dialog instead of replacing the page.
 *
 * One mutation per card is the right granularity: each card calls
 * `useDocumentDownload(praticaId)` and owns its own state.
 */
import { useMutation } from '@tanstack/react-query';
import type { FileType } from '@app/shared';
import { downloadDocument } from '../lib/archivio-client';

export type DownloadState = 'idle' | 'requesting' | 'error';

export function useDocumentDownload(praticaId: string) {
  const mutation = useMutation({
    mutationFn: (fileType: FileType) => downloadDocument(praticaId, { fileType }),
    onSuccess: (data) => {
      // Navigate the current tab to the presigned URL. The server returns the
      // file with Content-Disposition: attachment so this triggers a download,
      // not a navigation. Using assign() (NOT window.open) avoids popup
      // blocking — the mutation callback runs after async resolution, which
      // is no longer a "user gesture" context.
      window.location.assign(data.url);
    },
    retry: 1,
  });

  const state: DownloadState = mutation.isPending
    ? 'requesting'
    : mutation.isError
      ? 'error'
      : 'idle';

  return {
    download: (fileType: FileType) => mutation.mutate(fileType),
    state,
    error: mutation.error,
    reset: mutation.reset,
  };
}
